import React, { useState, useEffect, useRef } from "react";
import { Modal, Slider, Button, Space, Select, Row, Col, Spin, message } from "antd";
import { MapContainer, TileLayer, Marker, Circle, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { useAppContext } from "../../context";
import MapClickHandler from "../MappaClienti/MapClickHandler";
import { yellowIcon } from "../MappaClienti/markerIcons";
import { centerItalyPosition } from "../MappaClienti/constants";
import { fetchComuni, geocode } from "./api";

// Icona marker a badge che mostra il numero di documenti dell'org.
const countIcon = (count) =>
  L.divIcon({
    html: `<div style="background:#1890ff;color:#fff;border:2px solid #fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;box-shadow:0 1px 4px rgba(0,0,0,0.4)">${count}</div>`,
    className: "doc-count-marker",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

// Ricentra/zooma la mappa e ne ricalcola le dimensioni quando cambia il punto.
const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    if (center) map.setView([center.lat, center.lng], zoom || map.getZoom());
    return () => clearTimeout(t);
  }, [center, zoom, map]);
  return null;
};

// Modal per scegliere un'area (Regione/Provincia/Città o click sulla mappa) e un raggio (km).
const MapFilterModal = ({
  visible,
  onCancel,
  onApply,
  orgPoints = [],
  initial,
  regioni = [],
  province = [],
}) => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const [center, setCenter] = useState(initial?.center || null);
  const [zoom, setZoom] = useState(initial?.center ? 11 : 6);
  const [rangeKm, setRangeKm] = useState(initial?.rangeKm || 30);
  const [comuneOptions, setComuneOptions] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const searchTimer = useRef(null);

  // Geocoding della località selezionata → centro + zoom della mappa.
  const goTo = async (query, zoomLevel) => {
    if (!query) return;
    setGeocoding(true);
    try {
      const r = await geocode(query);
      if (r) {
        setCenter({ lat: r.lat, lng: r.lng });
        setZoom(zoomLevel);
      } else {
        message.warning(`Località non trovata: ${query}`);
      }
    } catch (e) {
      console.error("Errore geocoding:", e);
      message.error("Errore nel posizionamento sulla mappa");
    } finally {
      setGeocoding(false);
    }
  };

  // Ricerca comuni server-side (debounce).
  const onComuneSearch = (text) => {
    clearTimeout(searchTimer.current);
    if (!text || text.length < 2) {
      setComuneOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const list = await fetchComuni(env, text);
        setComuneOptions(
          list.slice(0, 30).map((c) => ({
            value: c.id,
            label: c.text,
            nome: c.nome,
            provincia: c.provincia_nome || c.provincia,
            lat: c.latitude,
            lng: c.longitude,
          }))
        );
      } catch (e) {
        console.error("Errore ricerca comuni:", e);
      }
    }, 400);
  };

  const handleMapClick = (latlng) => {
    setCenter({ lat: latlng.lat, lng: latlng.lng });
  };

  const apply = () => {
    if (!center) {
      message.warning("Scegli Regione/Provincia/Città o clicca sulla mappa");
      return;
    }
    onApply({ center, rangeKm });
  };

  return (
    <Modal
      title="Filtra per posizione"
      open={visible}
      onCancel={onCancel}
      width={1080}
      style={{ top: 20 }}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>Annulla</Button>,
        <Button key="apply" type="primary" onClick={apply}>Applica filtro</Button>,
      ]}
    >
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={8}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="Regione"
            options={regioni.map((r) => ({ value: r.nome, label: r.nome }))}
            onChange={(v) => v && goTo(`${v}, Italia`, 8)}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="Provincia"
            options={province.map((p) => ({ value: p.nome, label: p.text }))}
            onChange={(v) => v && goTo(`${v}, Italia`, 10)}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            allowClear
            showSearch
            filterOption={false}
            style={{ width: "100%" }}
            placeholder="Città"
            notFoundContent={null}
            onSearch={onComuneSearch}
            options={comuneOptions}
            onChange={(v, opt) => {
              if (!opt) return;
              // Coordinate dirette da select2 (/comuni); fallback a geocoding se assenti.
              if (opt.lat != null && opt.lng != null) {
                setCenter({ lat: opt.lat, lng: opt.lng });
                setZoom(12);
              } else {
                goTo(`${opt.nome}, ${opt.provincia}, Italia`, 12);
              }
            }}
          />
        </Col>
      </Row>

      <Space style={{ margin: "12px 0" }}>
        {geocoding && <Spin size="small" />}
        <span>Raggio: <strong>{rangeKm} km</strong></span>
      </Space>
      <Slider min={1} max={500} step={5} value={rangeKm} onChange={setRangeKm} />

      <div style={{ height: 600, width: "100%" }}>
        <MapContainer
          center={center ? [center.lat, center.lng] : centerItalyPosition}
          zoom={zoom}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap contributors"
          />
          <MapClickHandler onMapClick={handleMapClick} isSelectingLocation />
          <RecenterMap center={center} zoom={zoom} />
          {orgPoints.map((p) => (
            <Marker key={p.objectId} position={[p.lat, p.lng]} icon={countIcon(p.count)}>
              <Tooltip>
                {p.nome}: {p.count} {p.count === 1 ? "documento" : "documenti"}
              </Tooltip>
            </Marker>
          ))}
          {center && (
            <>
              <Marker position={[center.lat, center.lng]} icon={yellowIcon} />
              <Circle center={[center.lat, center.lng]} radius={rangeKm * 1000} />
            </>
          )}
        </MapContainer>
      </div>
      <p style={{ marginTop: 8, color: "#888" }}>
        Scegli Regione, Provincia o Città (la mappa si centra da sola) oppure clicca un punto sulla mappa.
      </p>
    </Modal>
  );
};

export default MapFilterModal;
